/**
 * ApplicationCard — Kanban card showing application summary
 * Epic 3B: Broker Application Workflow
 */

import { STAGE_DISPLAY, getSlaStatus, STAGE_SLA, getPhaseColor } from '../constants/applicationStages'
import type { Application } from '../api/brokerApi'
import { Clock, DollarSign, Building2 } from 'lucide-react'

interface ApplicationCardProps {
  application: Application
  onClick: (app: Application) => void
  compact?: boolean
}

function daysInStage(stageUpdatedAt: string | null): number {
  if (!stageUpdatedAt) return 0
  const diff = Date.now() - new Date(stageUpdatedAt).getTime()
  return Math.floor(diff / (1000 * 60 * 60 * 24))
}

const SLA_COLORS = {
  green: 'text-green-600',
  amber: 'text-amber-600',
  red: 'text-red-600',
}

export default function ApplicationCard({ application, onClick, compact: _compact }: ApplicationCardProps) {
  const days = daysInStage(application.stage_updated_at)
  const sla = getSlaStatus(application.stage, days)
  const phaseColor = getPhaseColor(application.stage)
  const initials = (application.customer_name || 'UN')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  const formatCurrency = (amount: number | null) => {
    if (!amount) return '—'
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  return (
    <div
      onClick={() => onClick(application)}
      className="bg-white rounded-lg border border-gray-200 p-3 cursor-pointer hover:shadow-md hover:border-gray-300 transition-all"
    >
      {/* Header: Avatar + Name */}
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
          style={{ backgroundColor: phaseColor }}
        >
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 truncate">
            {application.customer_name || 'Unknown'}
          </p>
        </div>
      </div>

      {/* Stage Badge */}
      <div className="mb-2">
        <span
          className="inline-block text-xs font-medium px-2 py-0.5 rounded-full text-white"
          style={{ backgroundColor: phaseColor }}
        >
          {STAGE_DISPLAY[application.stage] || application.stage}
        </span>
      </div>

      {/* Details Row */}
      <div className="flex items-center gap-3 text-xs text-gray-500">
        {application.loan_amount && (
          <span className="flex items-center gap-1">
            <DollarSign className="w-3 h-3" />
            {formatCurrency(application.loan_amount)}
          </span>
        )}
        {application.lender_name && (
          <span className="flex items-center gap-1 truncate">
            <Building2 className="w-3 h-3" />
            {application.lender_name}
          </span>
        )}
      </div>

      {/* SLA indicator */}
      {STAGE_SLA[application.stage] && (
        <div className={`flex items-center gap-1 mt-2 text-xs ${SLA_COLORS[sla]}`}>
          <Clock className="w-3 h-3" />
          <span>{days}d in stage</span>
          {sla === 'red' && <span className="font-semibold">(overdue)</span>}
        </div>
      )}
    </div>
  )
}
